import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../config/supabase';

const Concierge = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');
  const [destination, setDestination] = useState('');
  const [serviceDate, setServiceDate] = useState('');
  const [serviceTime, setServiceTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [userBookings, setUserBookings] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedBookingDetail, setSelectedBookingDetail] = useState(null);
  const [viewingDocument, setViewingDocument] = useState(null);

  // Car & CPO request states
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [requestCPO, setRequestCPO] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [vehiclesError, setVehiclesError] = useState('');
  
  // Geoapify autocomplete states
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [destinationSuggestions, setDestinationSuggestions] = useState([]);
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDestinationSuggestions, setShowDestinationSuggestions] = useState(false);
  const debounceTimerPickup = useRef(null);
  const debounceTimerDestination = useRef(null);

  // Set minimum date to today
  const minDate = new Date().toISOString().split('T')[0];

  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Geoapify address search for pickup
  const searchPickupAddress = async (query) => {
    if (query.length < 3) {
      setPickupSuggestions([]);
      setShowPickupSuggestions(false);
      return;
    }

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey) {
      console.error('Geoapify API key not found');
      return;
    }

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&apiKey=${apiKey}&limit=5`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setPickupSuggestions(data.features);
        setShowPickupSuggestions(true);
      } else {
        setPickupSuggestions([]);
        setShowPickupSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching pickup suggestions:', error);
      setPickupSuggestions([]);
      setShowPickupSuggestions(false);
    }
  };

  // Geoapify address search for destination
  const searchDestinationAddress = async (query) => {
    if (query.length < 3) {
      setDestinationSuggestions([]);
      setShowDestinationSuggestions(false);
      return;
    }

    const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
    if (!apiKey) {
      console.error('Geoapify API key not found');
      return;
    }

    try {
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&apiKey=${apiKey}&limit=5`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        setDestinationSuggestions(data.features);
        setShowDestinationSuggestions(true);
      } else {
        setDestinationSuggestions([]);
        setShowDestinationSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching destination suggestions:', error);
      setDestinationSuggestions([]);
      setShowDestinationSuggestions(false);
    }
  };

  const handlePickupInputChange = (value) => {
    setPickupAddress(value);
    setUseCurrentLocation(false);

    if (debounceTimerPickup.current) {
      clearTimeout(debounceTimerPickup.current);
    }

    debounceTimerPickup.current = setTimeout(() => {
      searchPickupAddress(value);
    }, 300);
  };

  const handleDestinationInputChange = (value) => {
    setDestination(value);

    if (debounceTimerDestination.current) {
      clearTimeout(debounceTimerDestination.current);
    }

    debounceTimerDestination.current = setTimeout(() => {
      searchDestinationAddress(value);
    }, 300);
  };

  const selectPickupAddress = (suggestion) => {
    setPickupAddress(suggestion.properties.formatted);
    setShowPickupSuggestions(false);
    setPickupSuggestions([]);
    setUseCurrentLocation(false);
  };

  const selectDestinationAddress = (suggestion) => {
    setDestination(suggestion.properties.formatted);
    setShowDestinationSuggestions(false);
    setDestinationSuggestions([]);
  };

  useEffect(() => {
    fetchAvailableVehicles();
    // Fetch user bookings if logged in
    if (user) {
      fetchUserBookings();
    }
  }, [user]);

  const fetchAvailableVehicles = async () => {
    setLoadingVehicles(true);
    setVehiclesError('');
    try {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('is_active', true)
        .order('make');

      if (error) throw error;
      setAvailableVehicles(data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setAvailableVehicles([]);
      setVehiclesError(error.message || 'Unable to load available vehicles.');
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchUserBookings = async () => {
    if (!user) return;
    
    setLoadingBookings(true);
    try {
      const { data, error } = await supabase
        .from('vip_protect_bookings')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUserBookings(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const downloadDocument = async (url, filename) => {
    try {
      // Fetch the file from Supabase
      const response = await fetch(url);
      if (!response.ok) throw new Error('Download failed');
      
      // Convert to blob
      const blob = await response.blob();
      
      // Create temporary URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary anchor and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      showNotification('Download started', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Download failed. Please try again.', 'error');
    }
  };

  const reverseGeocodeLocation = async (location, accuracyMeters) => {
    try {
      const apiKey = process.env.REACT_APP_GEOAPIFY_API_KEY;
      const response = await fetch(
        `https://api.geoapify.com/v1/geocode/reverse?lat=${location.lat}&lon=${location.lng}&apiKey=${apiKey}`
      );
      const data = await response.json();

      if (data.features && data.features[0]) {
        const address = data.features[0].properties.formatted;
        setPickupAddress(address);
        showNotification(`✓ Location detected (±${accuracyMeters}m accuracy): ${address}`, 'success');
      } else {
        setPickupAddress(`${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`);
        showNotification(`✓ Coordinates: ${location.lat.toFixed(6)}, ${location.lng.toFixed(6)} (±${accuracyMeters}m)`, 'success');
      }
    } catch (error) {
      console.error('Error getting address:', error);
      const coords = `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`;
      setPickupAddress(coords);
      showNotification(`✓ Location: ${coords} (±${accuracyMeters}m accuracy)`, 'success');
    }
  };

  const requestBrowserLocation = (options) => new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

  const getCurrentLocation = async () => {
    if (!navigator.geolocation) {
      showNotification('Geolocation is not supported by your browser', 'error');
      return;
    }

    setLoadingLocation(true);
    try {
      let position;

      try {
        position = await requestBrowserLocation({
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        });
      } catch (primaryError) {
        if (primaryError.code === 3) {
          position = await requestBrowserLocation({
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 60000
          });
        } else {
          throw primaryError;
        }
      }

      const location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      setCurrentLocation(location);

      const accuracyMeters = Math.round(position.coords.accuracy);
      await reverseGeocodeLocation(location, accuracyMeters);
      setUseCurrentLocation(true);
    } catch (error) {
      console.error('Error getting location:', error);
      let errorMessage = 'Unable to retrieve your location. ';

      if (error.code === 1) {
        errorMessage += 'Please allow location access in your browser settings.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out. Please try again or type your pickup address manually.';
      } else {
        errorMessage += 'Please enter your pickup address manually.';
      }

      showNotification(errorMessage, 'error');
    } finally {
      setLoadingLocation(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!(useCurrentLocation || pickupAddress) || !destination || !serviceDate || !serviceTime || !endTime) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    // Validate end time is after start time
    if (endTime <= serviceTime) {
      showNotification('End time must be after start time', 'error');
      return;
    }

    if (!user) {
      showNotification('Please login to book VIP services', 'error');
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    // Calculate duration in hours
    const start = new Date(`2000-01-01T${serviceTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const durationHours = (end - start) / (1000 * 60 * 60);

    try {
      const bookingData = {
        user_id: user.id,
        user_name: user.name,
        user_email: user.email,
        user_phone: user.phone || 'Not provided',
        pickup_address: pickupAddress,
        destination_address: destination,
        service_date: serviceDate,
        service_time: serviceTime,
        end_time: endTime,
        duration_hours: durationHours,
        requested_vehicle_id: selectedVehicleId || null,
        cpo_requested: requestCPO,
        status: 'pending'
      };

      const { data, error } = await supabase
        .from('vip_protect_bookings')
        .insert([bookingData]);

      if (error) throw error;

      showNotification(`VIP Protect booking submitted successfully! ${durationHours} hour(s) rental. We will contact you shortly to confirm.`, 'success');
      
      // Reset form
      setPickupAddress('');
      setDestination('');
      setServiceDate('');
      setServiceTime('');
      setEndTime('');
      setUseCurrentLocation(false);
      setCurrentLocation(null);
      setSelectedVehicleId('');
      setRequestCPO(false);
      
      // Refresh bookings
      fetchUserBookings();
      
    } catch (error) {
      console.error('Error submitting booking:', error);
      showNotification('Error submitting booking. Please try again or contact support.', 'error');
    }
  };

  return (
    <div className="page-container">
      {/* Notification Toast */}
      {notification.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          left: '20px',
          maxWidth: '500px',
          margin: '0 auto',
          padding: '1rem 1.5rem',
          background: notification.type === 'success' ? '#4CAF50' : notification.type === 'error' ? '#f44336' : 'var(--gold)',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ flex: 1, fontWeight: '500' }}>{notification.message}</div>
          <button
            onClick={() => setNotification({ show: false, message: '', type: '' })}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0',
              lineHeight: '1'
            }}
          >
            ×
          </button>
        </div>
      )}
      
      <div className="page-wrapper">
        <h1 className="page-title">London VIP Protect & Chauffeur Drive</h1>
        <div className="underline"></div>
        <p className="page-subtitle">Premium Chauffeur & Security Services</p>

        {/* Booking History Toggle */}
        {user && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: '2rem',
            padding: '0 1rem'
          }}>
            <div style={{
              display: 'inline-flex',
              background: 'white',
              border: '2px solid var(--gold)',
              borderRadius: '12px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(205, 182, 136, 0.2)'
            }}>
              <button
                type="button"
                onClick={() => setShowHistory(false)}
                style={{
                  padding: '0.75rem 2rem',
                  background: !showHistory ? 'var(--gold)' : 'transparent',
                  color: !showHistory ? 'black' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                New Booking
              </button>
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                style={{
                  padding: '0.75rem 2rem',
                  background: showHistory ? 'var(--gold)' : 'transparent',
                  color: showHistory ? 'black' : '#666',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
              >
                My Bookings
              </button>
            </div>
          </div>
        )}

        {/* Booking History Section */}
        {showHistory && user && (
          <div className="card" style={{ maxWidth: '900px', margin: '0 auto 2rem' }}>
            <h3 style={{ color: 'var(--gold)', marginBottom: '1.5rem' }}>
              My Bookings History
            </h3>
            {loadingBookings ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <LoadingSpinner />
              </div>
            ) : userBookings.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No bookings yet. Create your first booking below!</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {userBookings.map((booking) => {
                  const getStatusColor = (status) => {
                    switch (status) {
                      case 'confirmed': return '#4CAF50';
                      case 'pending': return 'var(--gold)';
                      case 'in_progress': return '#2196F3';
                      case 'completed': return '#666';
                      case 'cancelled': return '#f44336';
                      default: return '#999';
                    }
                  };

                  return (
                    <div
                      key={booking.id}
                      onClick={() => setSelectedBookingDetail(booking)}
                      style={{
                        padding: '1rem 1.25rem',
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderLeft: `4px solid ${getStatusColor(booking.status)}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                            <span style={{
                              padding: '0.2rem 0.6rem',
                              background: getStatusColor(booking.status),
                              color: booking.status === 'pending' ? 'black' : 'white',
                              borderRadius: '12px',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.03em'
                            }}>
                              {booking.status.replace('_', ' ')}
                            </span>
                            <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#000' }}>
                              {new Date(booking.service_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                            <span style={{ fontSize: '0.8rem', color: '#666' }}>
                              {booking.service_time}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.25rem' }}>
                            <strong style={{ color: 'var(--gold)' }}>From:</strong> {booking.pickup_address.length > 50 ? booking.pickup_address.substring(0, 50) + '...' : booking.pickup_address}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: '#555' }}>
                            <strong style={{ color: 'var(--gold)' }}>To:</strong> {booking.destination_address.length > 50 ? booking.destination_address.substring(0, 50) + '...' : booking.destination_address}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {(booking.invoice_url || booking.receipt_url) && (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'rgba(33, 150, 243, 0.1)', color: '#2196F3', borderRadius: '4px', fontWeight: 'bold' }}>
                              📄 Documents
                            </span>
                          )}
                          {booking.driver_name && (
                            <span style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', background: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50', borderRadius: '4px', fontWeight: 'bold' }}>
                              ✓ Driver Assigned
                            </span>
                          )}
                          <span style={{ color: '#999', fontSize: '1.2rem' }}>›</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Booking Detail Modal */}
        {selectedBookingDetail && (
          <div
            onClick={() => {
              setSelectedBookingDetail(null);
              setViewingDocument(null);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '1rem',
              overflowY: 'auto'
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                padding: '2rem',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ color: 'var(--gold)', margin: 0 }}>Booking Details</h3>
                <button
                  onClick={() => {
                    setSelectedBookingDetail(null);
                    setViewingDocument(null);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#999',
                    padding: 0
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Status & Date */}
                <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Service Information</h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div>
                      <strong style={{ color: '#666' }}>Status:</strong>{' '}
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        background: selectedBookingDetail.status === 'confirmed' ? '#4CAF50' : selectedBookingDetail.status === 'pending' ? 'var(--gold)' : selectedBookingDetail.status === 'in_progress' ? '#2196F3' : selectedBookingDetail.status === 'completed' ? '#666' : '#f44336',
                        color: selectedBookingDetail.status === 'pending' ? 'black' : 'white',
                        borderRadius: '12px',
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        textTransform: 'uppercase'
                      }}>
                        {selectedBookingDetail.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div>
                      <strong style={{ color: '#666' }}>Date:</strong>{' '}
                      <span>{new Date(selectedBookingDetail.service_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                    <div>
                      <strong style={{ color: '#666' }}>Time:</strong>{' '}
                      <span>{selectedBookingDetail.service_time} - {selectedBookingDetail.end_time}</span>
                    </div>
                    {selectedBookingDetail.duration_hours && (
                      <div>
                        <strong style={{ color: '#666' }}>Duration:</strong>{' '}
                        <span>{selectedBookingDetail.duration_hours} hour(s)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Route */}
                <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Route</h4>
                  <div style={{ display: 'grid', gap: '0.75rem' }}>
                    <div>
                      <strong style={{ color: '#666' }}>Pickup:</strong>
                      <div style={{ marginTop: '0.25rem' }}>{selectedBookingDetail.pickup_address}</div>
                    </div>
                    <div>
                      <strong style={{ color: '#666' }}>Destination:</strong>
                      <div style={{ marginTop: '0.25rem' }}>{selectedBookingDetail.destination_address}</div>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                {selectedBookingDetail.driver_name && (
                  <div style={{ padding: '1rem', background: 'rgba(205, 182, 136, 0.1)', border: '2px solid var(--gold)', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: 'var(--gold)' }}>Your Driver</h4>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'start' }}>
                      {selectedBookingDetail.driver_photo_url && (
                        <img
                          src={selectedBookingDetail.driver_photo_url}
                          alt={selectedBookingDetail.driver_name}
                          style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '2px solid var(--gold)',
                            flexShrink: 0
                          }}
                        />
                      )}
                      <div>
                        <div style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>{selectedBookingDetail.driver_name}</div>
                        <div style={{ color: '#666', marginTop: '0.25rem' }}>{selectedBookingDetail.driver_phone}</div>
                        {selectedBookingDetail.vehicle_make && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.95rem' }}>
                            <strong>Vehicle:</strong> {selectedBookingDetail.vehicle_color} {selectedBookingDetail.vehicle_make} {selectedBookingDetail.vehicle_model}
                            <div style={{ fontWeight: 'bold', color: 'var(--gold)', marginTop: '0.25rem' }}>{selectedBookingDetail.vehicle_plate}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Requests */}
                {(selectedBookingDetail.requested_vehicle_id || selectedBookingDetail.cpo_requested) && (
                  <div style={{ padding: '1rem', background: 'rgba(205, 182, 136, 0.08)', border: '1px solid rgba(205,182,136,0.4)', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 0.75rem 0', color: '#000' }}>Your Requests</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {selectedBookingDetail.requested_vehicle_id && availableVehicles.find(v => v.id === selectedBookingDetail.requested_vehicle_id) && (() => {
                        const rv = availableVehicles.find(v => v.id === selectedBookingDetail.requested_vehicle_id);
                        return (
                          <div style={{ fontSize: '0.95rem' }}>
                            <strong style={{ color: '#666' }}>Requested Vehicle:</strong>{' '}
                            <span>{rv.color} {rv.make} {rv.model} · {rv.plate}</span>
                          </div>
                        );
                      })()}
                      {selectedBookingDetail.cpo_requested && (
                        <div style={{ fontSize: '0.95rem' }}>
                          <strong style={{ color: '#666' }}>CPO:</strong>{' '}
                          <span>🛡 Close Protection Officer requested</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Transaction Documents */}
                {(selectedBookingDetail.invoice_url || selectedBookingDetail.receipt_url) && (
                  <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 1rem 0', color: '#000' }}>Transaction Documents</h4>
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {selectedBookingDetail.invoice_url && (
                        <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                            <div>
                              <div style={{ fontWeight: 'bold', color: '#000' }}>Invoice</div>
                              {selectedBookingDetail.invoice_uploaded_at && (
                                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                  Uploaded: {new Date(selectedBookingDetail.invoice_uploaded_at).toLocaleDateString('en-GB')}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => setViewingDocument({ url: selectedBookingDetail.invoice_url, type: 'invoice' })}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  const filename = `invoice_${selectedBookingDetail.id}.pdf`;
                                  downloadDocument(selectedBookingDetail.invoice_url, filename);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'var(--gold)',
                                  color: 'black',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                          {viewingDocument?.type === 'invoice' && (() => {
                            const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)($|\?)/i.test(selectedBookingDetail.invoice_url);
                            const isPdf = /\.pdf($|\?)/i.test(selectedBookingDetail.invoice_url);
                            
                            return (
                              <div style={{ padding: '1rem', background: '#fff' }}>
                                {isImage ? (
                                  <img
                                    src={selectedBookingDetail.invoice_url}
                                    alt="Invoice"
                                    style={{ width: '100%', height: 'auto', borderRadius: '4px', border: '1px solid #ddd' }}
                                  />
                                ) : isPdf ? (
                                  <iframe
                                    src={selectedBookingDetail.invoice_url}
                                    title="Invoice PDF"
                                    style={{ width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: '4px' }}
                                  />
                                ) : (
                                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    <p>Preview not available for this file type.</p>
                                    <a
                                      href={selectedBookingDetail.invoice_url}
                                      download
                                      style={{ color: '#2196F3', textDecoration: 'underline' }}
                                    >
                                      Click here to download
                                    </a>
                                  </div>
                                )}
                                <button
                                  onClick={() => setViewingDocument(null)}
                                  style={{
                                    marginTop: '0.75rem',
                                    padding: '0.5rem 1rem',
                                    background: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Close Preview
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {selectedBookingDetail.receipt_url && (
                        <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderBottom: '1px solid #e0e0e0' }}>
                            <div>
                              <div style={{ fontWeight: 'bold', color: '#000' }}>Receipt</div>
                              {selectedBookingDetail.receipt_uploaded_at && (
                                <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                                  Uploaded: {new Date(selectedBookingDetail.receipt_uploaded_at).toLocaleDateString('en-GB')}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button
                                onClick={() => setViewingDocument({ url: selectedBookingDetail.receipt_url, type: 'receipt' })}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: '#2196F3',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold'
                                }}
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  const filename = `receipt_${selectedBookingDetail.id}.pdf`;
                                  downloadDocument(selectedBookingDetail.receipt_url, filename);
                                }}
                                style={{
                                  padding: '0.5rem 1rem',
                                  background: 'var(--gold)',
                                  color: 'black',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '0.9rem',
                                  fontWeight: 'bold',
                                  cursor: 'pointer'
                                }}
                              >
                                Download
                              </button>
                            </div>
                          </div>
                          {viewingDocument?.type === 'receipt' && (() => {
                            const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)($|\?)/i.test(selectedBookingDetail.receipt_url);
                            const isPdf = /\.pdf($|\?)/i.test(selectedBookingDetail.receipt_url);
                            
                            return (
                              <div style={{ padding: '1rem', background: '#fff' }}>
                                {isImage ? (
                                  <img
                                    src={selectedBookingDetail.receipt_url}
                                    alt="Receipt"
                                    style={{ width: '100%', height: 'auto', borderRadius: '4px', border: '1px solid #ddd' }}
                                  />
                                ) : isPdf ? (
                                  <iframe
                                    src={selectedBookingDetail.receipt_url}
                                    title="Receipt PDF"
                                    style={{ width: '100%', height: '600px', border: '1px solid #ddd', borderRadius: '4px' }}
                                  />
                                ) : (
                                  <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                                    <p>Preview not available for this file type.</p>
                                    <a
                                      href={selectedBookingDetail.receipt_url}
                                      download
                                      style={{ color: '#2196F3', textDecoration: 'underline' }}
                                    >
                                      Click here to download
                                    </a>
                                  </div>
                                )}
                                <button
                                  onClick={() => setViewingDocument(null)}
                                  style={{
                                    marginTop: '0.75rem',
                                    padding: '0.5rem 1rem',
                                    background: '#f0f0f0',
                                    color: '#333',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  Close Preview
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {selectedBookingDetail.notes && (
                  <div style={{ padding: '1rem', background: '#f8f8f8', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#000' }}>Admin Notes</h4>
                    <div style={{ fontSize: '0.95rem', color: '#666' }}>{selectedBookingDetail.notes}</div>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setSelectedBookingDetail(null);
                    setViewingDocument(null);
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: 'var(--gold)',
                    border: 'none',
                    color: 'black',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {!showHistory && (
        <form onSubmit={handleSubmit} className="card" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h3 style={{ color: '#000', marginBottom: '1.5rem' }}>Book Your Service</h3>

          {/* Pickup Location Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h4 style={{ color: '#000', marginBottom: '1rem', fontSize: '1.1rem' }}>Pickup Location</h4>
            
            <button
              type="button"
              onClick={getCurrentLocation}
              disabled={loadingLocation}
              style={{
                width: '100%',
                padding: '0.75rem',
                marginBottom: '1rem',
                background: useCurrentLocation ? 'var(--gold)' : 'transparent',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: useCurrentLocation ? 'black' : 'var(--gold)',
                cursor: loadingLocation ? 'not-allowed' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {loadingLocation ? 'Getting Location...' : useCurrentLocation ? '✓ Using Current Location' : 'Use My Current Location'}
            </button>

            <div style={{ textAlign: 'center', margin: '1rem 0', color: '#666' }}>OR</div>

            <div className="form-group" style={{ position: 'relative' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Enter Pickup Address *
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={pickupAddress}
                  onChange={(e) => handlePickupInputChange(e.target.value)}
                  onFocus={() => { if (pickupAddress.length >= 3) searchPickupAddress(pickupAddress); }}
                  placeholder="Start typing your address..."
                  autoComplete="off"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid var(--gold)',
                    borderRadius: '4px',
                    color: '#000'
                  }}
                />
                {showPickupSuggestions && pickupSuggestions.length > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    marginTop: '4px'
                  }}>
                    {pickupSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => selectPickupAddress(suggestion)}
                        style={{
                          padding: '10px 15px',
                          cursor: 'pointer',
                          borderBottom: index < pickupSuggestions.length - 1 ? '1px solid #eee' : 'none',
                          fontSize: '14px',
                          color: '#000'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                      >
                        {suggestion.properties.formatted}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Destination */}
          <div className="form-group" style={{ marginBottom: '1.5rem', position: 'relative' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              Destination Address *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={destination}
                onChange={(e) => handleDestinationInputChange(e.target.value)}
                onFocus={() => { if (destination.length >= 3) searchDestinationAddress(destination); }}
                required
                autoComplete="off"
                placeholder="Start typing destination..."
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            />
            {showDestinationSuggestions && destinationSuggestions.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                marginTop: '4px'
              }}>
                {destinationSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    onClick={() => selectDestinationAddress(suggestion)}
                    style={{
                      padding: '10px 15px',
                      cursor: 'pointer',
                      borderBottom: index < destinationSuggestions.length - 1 ? '1px solid #eee' : 'none',
                      fontSize: '14px',
                      color: '#000'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {suggestion.properties.formatted}
                  </div>
                ))}
              </div>
            )}
          </div>
          </div>

          {/* Date and Time */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Service Date *
              </label>
              <input
                type="date"
                value={serviceDate}
                onChange={(e) => {
                  e.stopPropagation();
                  setServiceDate(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                min={minDate}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
                Start Time *
              </label>
              <input
                type="time"
                value={serviceTime}
                onChange={(e) => {
                  e.stopPropagation();
                  setServiceTime(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid var(--gold)',
                  borderRadius: '4px',
                  color: '#000'
                }}
              />
            </div>
          </div>

          {/* End Time / Duration */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#000' }}>
              End Time (Duration) *
            </label>
            <input
              type="time"
              value={endTime}
              onChange={(e) => {
                e.stopPropagation();
                setEndTime(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid var(--gold)',
                borderRadius: '4px',
                color: '#000'
              }}
            />
            {serviceTime && endTime && endTime > serviceTime && (
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'rgba(205, 182, 136, 0.1)', borderRadius: '4px', color: 'var(--gold)', fontSize: '0.9rem', fontWeight: 'bold' }}>
                ⏱ Duration: {(() => {
                  const start = new Date(`2000-01-01T${serviceTime}`);
                  const end = new Date(`2000-01-01T${endTime}`);
                  const hours = (end - start) / (1000 * 60 * 60);
                  return `${hours.toFixed(1)} hour(s)`;
                })()}
              </div>
            )}
          </div>

          {/* Vehicle Selection */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
              <h4 style={{ color: '#000', margin: 0, fontSize: '1.1rem' }}>Request a Vehicle</h4>
              {selectedVehicleId && (
                <span style={{ fontSize: '0.78rem', color: '#000', background: 'rgba(205, 182, 136, 0.18)', border: '1px solid var(--gold)', borderRadius: '999px', padding: '0.28rem 0.65rem', fontWeight: 'bold', letterSpacing: '0.02em' }}>
                  Vehicle selected
                </span>
              )}
            </div>
            {loadingVehicles ? (
              <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Loading available vehicles...</div>
            ) : vehiclesError ? (
              <div style={{ padding: '0.75rem', background: 'rgba(244, 67, 54, 0.08)', border: '1px solid #f44336', borderRadius: '4px', color: '#b71c1c', fontSize: '0.9rem' }}>
                Unable to load available vehicles: {vehiclesError}
              </div>
            ) : availableVehicles.length === 0 ? (
              <div style={{ padding: '0.75rem', background: 'rgba(205, 182, 136, 0.08)', border: '1px solid var(--gold)', borderRadius: '4px', color: '#666', fontSize: '0.9rem' }}>
                No vehicles currently listed — our team will assign one for you.
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.9rem' }}>
                {availableVehicles.map((vehicle) => {
                  const isSelected = selectedVehicleId === vehicle.id;

                  return (
                    <button
                      key={vehicle.id}
                      type="button"
                      onClick={() => setSelectedVehicleId(isSelected ? '' : vehicle.id)}
                      style={{
                        padding: '0',
                        background: '#fff',
                        border: `2px solid ${isSelected ? 'var(--gold)' : '#ddd'}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                        boxShadow: isSelected ? '0 8px 18px rgba(205, 182, 136, 0.35)' : '0 3px 10px rgba(0,0,0,0.06)'
                      }}
                    >
                      <div style={{ position: 'relative', height: '140px', background: '#f4f4f4' }}>
                        {vehicle.vehicle_image_url ? (
                          <img
                            src={vehicle.vehicle_image_url}
                            alt={`${vehicle.make} ${vehicle.model}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <div style={{ width: '100%', height: '100%', color: '#777', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold', letterSpacing: '0.04em' }}>
                            IMAGE COMING SOON
                          </div>
                        )}

                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.08) 55%, rgba(0,0,0,0))'
                        }} />

                        <div style={{
                          position: 'absolute',
                          top: '0.6rem',
                          right: '0.6rem',
                          width: '24px',
                          height: '24px',
                          borderRadius: '999px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: isSelected ? 'var(--gold)' : 'rgba(255,255,255,0.8)',
                          border: `2px solid ${isSelected ? 'var(--gold)' : 'rgba(0,0,0,0.2)'}`,
                          color: isSelected ? '#000' : 'transparent',
                          fontWeight: 'bold',
                          fontSize: '0.8rem'
                        }}>
                          ✓
                        </div>

                        <div style={{ position: 'absolute', left: '0.75rem', right: '0.75rem', bottom: '0.65rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                          <div style={{ color: '#fff', fontWeight: 'bold', fontSize: '0.95rem', textShadow: '0 1px 2px rgba(0,0,0,0.45)' }}>
                            {vehicle.make} {vehicle.model}
                          </div>
                          {isSelected && (
                            <span style={{ background: 'var(--gold)', color: '#000', fontSize: '0.75rem', fontWeight: 'bold', borderRadius: '999px', padding: '0.22rem 0.5rem' }}>
                              Selected
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ padding: '0.8rem 0.85rem 0.9rem' }}>
                        <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                          <span style={{ fontSize: '0.75rem', color: '#333', background: '#f2f2f2', borderRadius: '999px', padding: '0.22rem 0.6rem', fontWeight: '600' }}>{vehicle.color}</span>
                          {vehicle.year && (
                            <span style={{ fontSize: '0.75rem', color: '#333', background: '#f2f2f2', borderRadius: '999px', padding: '0.22rem 0.6rem', fontWeight: '600' }}>{vehicle.year}</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.82rem', color: 'var(--gold)', fontWeight: 'bold', letterSpacing: '0.06em' }}>
                          {vehicle.plate}
                        </div>
                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: isSelected ? '#000' : '#888', fontWeight: isSelected ? 'bold' : '500' }}>
                          {isSelected ? 'Currently selected' : 'Tap to select'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedVehicleId && (() => {
              const selectedVehicle = availableVehicles.find(v => v.id === selectedVehicleId);
              if (!selectedVehicle) return null;
              return (
                <div style={{ marginTop: '0.9rem', padding: '0.8rem', border: '2px solid var(--gold)', borderRadius: '10px', background: 'rgba(205, 182, 136, 0.12)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  {selectedVehicle.vehicle_image_url ? (
                    <img
                      src={selectedVehicle.vehicle_image_url}
                      alt={`${selectedVehicle.make} ${selectedVehicle.model}`}
                      style={{ width: '72px', height: '52px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #ddd', flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: '72px', height: '52px', borderRadius: '8px', border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#888', fontSize: '0.68rem', fontWeight: 'bold', flexShrink: 0 }}>NO IMAGE</div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.73rem', color: '#7d6a3f', fontWeight: 'bold', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Selected Vehicle</div>
                    <div style={{ fontSize: '0.96rem', color: '#000', fontWeight: 'bold' }}>{selectedVehicle.make} {selectedVehicle.model}</div>
                    <div style={{ fontSize: '0.82rem', color: '#444' }}>{selectedVehicle.color}{selectedVehicle.year ? ` · ${selectedVehicle.year}` : ''} · {selectedVehicle.plate}</div>
                  </div>
                </div>
              );
            })()}
            {selectedVehicleId && (
              <button
                type="button"
                onClick={() => setSelectedVehicleId('')}
                style={{ marginTop: '0.5rem', background: 'none', border: 'none', color: '#999', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear selection
              </button>
            )}
          </div>

          {/* CPO Request */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.75rem',
              cursor: 'pointer',
              padding: '1rem',
              background: requestCPO ? 'rgba(205, 182, 136, 0.12)' : 'rgba(0,0,0,0.02)',
              border: `2px solid ${requestCPO ? 'var(--gold)' : '#ddd'}`,
              borderRadius: '8px',
              transition: 'all 0.2s ease'
            }}>
              <input
                type="checkbox"
                checked={requestCPO}
                onChange={(e) => setRequestCPO(e.target.checked)}
                style={{ marginTop: '2px', width: '18px', height: '18px', accentColor: 'var(--gold)', flexShrink: 0, cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontWeight: 'bold', color: '#000', fontSize: '1rem' }}>Add Close Protection Officer (CPO)</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>Request a dedicated security officer to accompany your journey for enhanced personal protection.</div>
              </div>
            </label>
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '1rem',
              background: 'var(--gold)',
              color: 'black',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Request VIP Service
          </button>
        </form>
        )}

        <div className="card" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>Service Features</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', textAlign: 'left' }}>
            <div>
              <strong style={{ color: '#000' }}>✓ Professional Chauffeurs</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Trained & Licensed</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ Luxury Vehicles</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>BMW i7 Fleet</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ VIP Security</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Secure Transport</p>
            </div>
            <div>
              <strong style={{ color: '#000' }}>✓ 24/7 Service</strong>
              <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>Always Available</p>
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Concierge;
